import React, { useState } from 'react';
import axios from 'axios';
import { useI18n } from '@/lib/i18n';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '@/components/ui/dialog';
import { X, ArrowRight, CheckCircle } from '@phosphor-icons/react';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function ApplicationDialog({ open, onOpenChange }) {
  const { t } = useI18n();
  const [form, setForm] = useState({ name: '', email: '', phone: '', position: '', message: '' });
  const [file, setFile] = useState(null);
  const [sending, setSending] = useState(false);
  const [done, setDone] = useState(false);

  const update = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const submit = async (e) => {
    e.preventDefault();
    setSending(true);
    try {
      const fd = new FormData();
      Object.entries(form).forEach(([k, v]) => fd.append(k, v));
      if (file) fd.append('resume', file);
      const res = await axios.post(`${API}/application`, fd, { headers: { 'Content-Type': 'multipart/form-data' } });
      if (res.data?.status === 'success') {
        setDone(true);
        toast.success(t.apply.success);
        setForm({ name: '', email: '', phone: '', position: '', message: '' });
        setFile(null);
      } else {
        toast.error(t.apply.error);
      }
    } catch {
      toast.error(t.apply.error);
    } finally {
      setSending(false);
    }
  };

  const close = () => {
    onOpenChange(false);
    setTimeout(() => setDone(false), 300);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        data-testid="application-dialog"
        className="max-w-2xl max-h-[90vh] overflow-y-auto bg-white p-0 border-0 rounded-none gap-0"
      >
        <button
          onClick={close}
          data-testid="application-close"
          className="absolute top-5 right-5 z-10 w-9 h-9 flex items-center justify-center hover:bg-[#F8F9FA] transition-colors"
          aria-label="close"
        >
          <X size={18} />
        </button>

        <DialogHeader className="p-8 md:p-10 pb-2">
          <div className="eyebrow text-[#C5A059]">De Melo Recruiting</div>
          <DialogTitle className="heading-display text-3xl text-[#0A1128] mt-3">{t.apply.title}</DialogTitle>
          <DialogDescription className="text-[#1E2229]/70 mt-2">{t.apply.sub}</DialogDescription>
        </DialogHeader>

        {done ? (
          <div className="p-10 text-center">
            <CheckCircle size={56} weight="thin" className="text-[#C5A059] mx-auto" />
            <h3 className="font-display text-2xl text-[#0A1128] mt-4">{t.apply.success}</h3>
            <button
              onClick={close}
              data-testid="application-done-close"
              className="mt-8 inline-flex items-center bg-[#0A1128] text-white px-6 py-3 text-[11px] font-semibold tracking-[0.22em] uppercase"
            >
              OK
            </button>
          </div>
        ) : (
          <form onSubmit={submit} className="p-8 md:p-10 pt-4 grid grid-cols-1 md:grid-cols-2 gap-x-8 gap-y-5">
            <DField label={t.apply.name} required testid="apply-name">
              <input required value={form.name} onChange={(e) => update('name', e.target.value)} className="apply-input" />
            </DField>
            <DField label={t.apply.email} required testid="apply-email">
              <input required type="email" value={form.email} onChange={(e) => update('email', e.target.value)} className="apply-input" />
            </DField>
            <DField label={t.apply.phone} testid="apply-phone">
              <input type="tel" value={form.phone} onChange={(e) => update('phone', e.target.value)} className="apply-input" />
            </DField>
            <DField label={t.apply.position} testid="apply-position">
              <input value={form.position} onChange={(e) => update('position', e.target.value)} className="apply-input" />
            </DField>
            <div className="md:col-span-2">
              <DField label={t.apply.message} testid="apply-message">
                <textarea rows={4} value={form.message} onChange={(e) => update('message', e.target.value)} className="apply-input resize-none" />
              </DField>
            </div>
            <div className="md:col-span-2">
              <DField label={t.apply.resume} testid="apply-resume">
                <input
                  type="file"
                  accept="application/pdf"
                  onChange={(e) => setFile(e.target.files?.[0] || null)}
                  className="block w-full text-sm text-[#1E2229] mt-2"
                />
              </DField>
            </div>

            <div className="md:col-span-2 pt-2">
              <button
                type="submit"
                disabled={sending}
                data-testid="application-submit"
                className="group inline-flex items-center gap-3 bg-[#C5A059] hover:bg-[#B38F4D] text-[#0A1128] px-8 py-4 text-[12px] font-semibold tracking-[0.22em] uppercase transition-all disabled:opacity-60"
              >
                {sending ? t.apply.sending : t.apply.submit}
                <ArrowRight size={16} weight="bold" className="group-hover:translate-x-1 transition-transform" />
              </button>
            </div>
          </form>
        )}

        <style>{`
          .apply-input {
            width: 100%;
            background: transparent;
            border: 0;
            border-bottom: 1px solid #E2E8F0;
            padding: 10px 0;
            font-size: 15px;
            color: #0A1128;
            outline: none;
            transition: border-color .3s ease;
          }
          .apply-input:focus { border-color: #C5A059; }
        `}</style>
      </DialogContent>
    </Dialog>
  );
}

function DField({ label, children, required, testid }) {
  return (
    <label className="block" data-testid={testid}>
      <span className="eyebrow text-[#64748B]">{label}{required && <span className="text-[#C5A059]"> *</span>}</span>
      {children}
    </label>
  );
}
